import axios, { AxiosError, AxiosRequestConfig } from "axios";
import store from "../redux/store";
import { setLoading } from "../redux/loadingSlice";
import { toast } from "react-toastify";
import { login, logout } from "../redux/authSlice";
import { CONFIG } from "../config";
import { isTokenExpiringBeyond8Days } from "./Auth";

const URL = `${CONFIG?.BASE_URL}/api`;

let refreshPromise: Promise<string> | null = null;

function startRefresh(): Promise<string> {
	return axios
		.get(`${URL}/user/refresh-token`, {
			withCredentials: true,
		})
		.then((res) => res.data as string); // the new JWT
}

function queueRefresh(): Promise<string> {
	if (!refreshPromise) {
		refreshPromise = startRefresh().finally(() => (refreshPromise = null)); // reset after it settles
	}
	return refreshPromise;
}

/** true if the access token will expire within N seconds */
function isExpiringSoon(token: string, seconds: number): boolean {
	try {
		const [, payload] = token.split(".");
		const { exp } = JSON.parse(atob(payload));
		return exp * 1000 - Date.now() < seconds * 1000;
	} catch {
		return false;
	}
}

// --------------------------------------------------
// instance
// --------------------------------------------------
const api = axios.create({
	baseURL: URL,
	timeout: 9000 * 1000,
	withCredentials: true,
});
api.defaults.timeout = 9000 * 1000;
api.defaults.withCredentials = true;

// ---------------- request interceptor ----------------
api.interceptors.request.use(async (config) => {
	const token = localStorage.getItem("token");
	if (token) {
		config.headers.Authorization = `Bearer ${token}`;
	}
	store.dispatch(setLoading({ isLoading: true }));

	// OPTIONAL: proactive refresh 30 s before expiry
	if (token && (isExpiringSoon(token, 30) || isTokenExpiringBeyond8Days())) {
		try {
			const newToken = await queueRefresh();
			localStorage.setItem("token", newToken);
			store.dispatch(login({ token: newToken }));
			config.headers.Authorization = `Bearer ${newToken}`;
		} catch (err) {
			store.dispatch(logout());
			return Promise.reject(err);
		}
	}

	return config;
});

// ---------------- response interceptor ----------------
api.interceptors.response.use(
	(res) => {
		store.dispatch(setLoading({ isLoading: false }));
		return res;
	},
	async (error: AxiosError) => {
		store.dispatch(setLoading({ isLoading: false }));

		if (
			!error.config?.url?.includes("/user/refresh-token") &&
			error.response?.data
		) {
			const data = error.response.data as unknown;
			const message =
				typeof data === "string"
					? data
					: (data as { detail?: string; title?: string })?.detail ??
					  (data as { detail?: string; title?: string })?.title ??
					  "Something went wrong";
			toast.error(message);
		}

		// short–circuit if it's the refresh call itself
		if (error.config?.url?.includes("/user/refresh-token")) {
			store.dispatch(logout());
			return Promise.reject(error);
		}

		const originalRequest = error.config as AxiosRequestConfig & {
			_retry?: boolean;
		};

		// plain network error or non‑401 – just bubble up
		if (!error.response || error.response.status !== 401) {
			return Promise.reject(error);
		}

		if (originalRequest._retry) {
			return Promise.reject(error);
		}

		originalRequest._retry = true;

		try {
			const newToken = await queueRefresh();
			localStorage.setItem("token", newToken);
			store.dispatch(login({ token: newToken }));

			originalRequest.headers = {
				...originalRequest.headers,
				Authorization: `Bearer ${newToken}`,
			};
			return api(originalRequest);
		} catch (refreshErr: any) {
			store.dispatch(logout());
			return Promise.reject(refreshErr);
		}
	}
);

export default api;
